import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
const LucideIconsMap = { Loader2, Send, CheckCircle2 };
import { useTracker } from "@/lib/tracker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORG_TYPES = [
  { v: "mental_health_clinic", l: "Mental health clinic / practice" },
  { v: "hospital", l: "Hospital / healthcare system" },
  { v: "university", l: "University / educational institution" },
  { v: "corporate", l: "Corporation / enterprise" },
  { v: "hr_recruitment", l: "HR / wellbeing team" },
  { v: "research", l: "Research / innovation partner" },
  { v: "government", l: "Government / municipality" },
  { v: "investor", l: "Investor / VC" },
  { v: "other", l: "Other" },
];

const INTERESTS = [
  "Clinical assessment workflow",
  "Student wellbeing support",
  "Employee wellbeing program",
  "Municipal innovation pilot",
  "Research collaboration",
  "Partnership / integration",
  "Other",
];

export default function PilotInquiryForm() {
  const { track } = useTracker();
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    company_type: "",
    role: "",
    country: "",
    pilot_interest: "",
    expected_users: "",
    message: "",
    consent: false,
    website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Please acknowledge the pilot boundaries before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.organization.trim() || undefined,
        company_type: form.company_type || "other",
        message: [
          `Role: ${form.role || "—"}`,
          `Country: ${form.country || "—"}`,
          `Pilot interest: ${form.pilot_interest || "—"}`,
          `Expected participants: ${form.expected_users || "—"}`,
          "",
          form.message.trim(),
        ].join("\n"),
        phone: undefined,
        source_page: typeof window !== "undefined" ? window.location.pathname : "/pilot-program",
        request_type: "pilot_inquiry",
        website: form.website, // honeypot
      };
      await axios.post(`${API}/contact`, payload);
      track && track("form_submit", { form: "pilot-inquiry" });
      setSubmitted(true);
      toast.success("Thank you — your pilot inquiry was received.");
      setForm({
        name: "", email: "", organization: "", company_type: "", role: "", country: "",
        pilot_interest: "", expected_users: "", message: "", consent: false, website: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not submit the form. Please try again or email us directly.";
      setError(typeof detail === "string" ? detail : "Could not submit the form.");
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="max-w-2xl mx-auto bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center"
        data-testid="pilot-form-success"
      >
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-6 h-6 text-emerald-700" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">
          Thank you — your inquiry was received
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          A member of the PsyTech team will review your pilot interest and get back to you within a few business days.
        </p>
        <Button
          variant="outline"
          className="mt-6 rounded-full"
          onClick={() => setSubmitted(false)}
          data-testid="pilot-form-reset"
        >
          Submit another inquiry
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5"
      data-testid="pilot-inquiry-form"
    >
      {/* Honeypot */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={(e) => set("website", e.target.value)}
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        className="hidden"
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Full name *" testid="pilot-name">
          <input
            type="text" required value={form.name} onChange={(e) => set("name", e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            data-testid="pilot-name-input"
          />
        </Field>
        <Field label="Work email *" testid="pilot-email">
          <input
            type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            data-testid="pilot-email-input"
          />
        </Field>
        <Field label="Organization *">
          <input
            type="text" required value={form.organization} onChange={(e) => set("organization", e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            data-testid="pilot-organization-input"
          />
        </Field>
        <Field label="Organization type *">
          <select
            required value={form.company_type} onChange={(e) => set("company_type", e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            data-testid="pilot-company-type-select"
          >
            <option value="">Select…</option>
            {ORG_TYPES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <Field label="Your role / title">
          <input
            type="text" value={form.role} onChange={(e) => set("role", e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            data-testid="pilot-role-input"
          />
        </Field>
        <Field label="Country">
          <input
            type="text" value={form.country} onChange={(e) => set("country", e.target.value)}
            placeholder="Netherlands, Germany, …"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            data-testid="pilot-country-input"
          />
        </Field>
        <Field label="Pilot interest">
          <select
            value={form.pilot_interest} onChange={(e) => set("pilot_interest", e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            data-testid="pilot-interest-select"
          >
            <option value="">Select…</option>
            {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
        <Field label="Expected participants">
          <input
            type="text" value={form.expected_users} onChange={(e) => set("expected_users", e.target.value)}
            placeholder="e.g. 20–50 professionals"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            data-testid="pilot-expected-users-input"
          />
        </Field>
      </div>

      <Field label="Tell us about your use case *">
        <textarea
          required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)}
          placeholder="What are you hoping to explore? What context are you operating in?"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none resize-y"
          data-testid="pilot-message-input"
        />
      </Field>

      <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
        <input
          type="checkbox" required checked={form.consent} onChange={(e) => set("consent", e.target.checked)}
          className="mt-1 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
          data-testid="pilot-consent-checkbox"
        />
        <span>
          I understand that PsyTech is not an emergency service and does not provide diagnosis or treatment.
          Pilot activity should follow appropriate consent, privacy, and professional oversight practices.
        </span>
      </label>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          We use your contact details only to respond to your inquiry.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          className="rounded-full bg-cyan-700 hover:bg-cyan-800 text-white px-7"
          data-testid="pilot-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Send pilot inquiry
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">{label}</span>
      {children}
    </label>
  );
}
